/**
 * Firestore IndexedDB persistence for the installed Firebase JS SDK 9.23.0.
 *
 * 9.23.0 exports the modern cache constructors (`initializeFirestore` +
 * `persistentLocalCache` + `persistentMultipleTabManager`). Those configure
 * persistence at instance construction, which avoids the race inherent in the
 * older `enableIndexedDbPersistence` / `enableMultiTabIndexedDbPersistence`
 * helpers (those must run before any other Firestore use).
 *
 * Persistence is an optimization, not a correctness dependency. Failure to
 * enable IndexedDB must never block startup; the app continues on ordinary
 * Firestore (memory / default).
 */

export const FIRESTORE_PERSISTENCE_MODES = {
  PERSISTENT: 'persistent',
  MEMORY: 'memory',
  FALLBACK: 'fallback',
};

let initialized = null;

function defaultState() {
  return {
    mode: FIRESTORE_PERSISTENCE_MODES.FALLBACK,
    enabled: false,
    multiTab: false,
    reason: null,
    initialized: false,
  };
}

function sanitizeReason(error) {
  if (!error) return null;
  const code = typeof error.code === 'string' ? error.code : null;
  const message = typeof error.message === 'string'
    ? error.message.slice(0, 160)
    : 'unknown';
  return code ? `${code}: ${message}` : message;
}

function isAlreadyInitialized(error) {
  const code = typeof error?.code === 'string' ? error.code : '';
  const message = typeof error?.message === 'string' ? error.message : '';
  return code.includes('already-initialized')
    || code.includes('already-exists')
    || /already (been )?initialized/i.test(message);
}

function logPersistenceState(state, logger) {
  if (typeof logger !== 'function') return;
  logger('[library-persistence]', {
    mode: state.mode,
    enabled: state.enabled,
    multiTab: state.multiTab,
    reason: state.reason,
  });
}

function defaultLogger() {
  const enabled = process.env.NODE_ENV === 'development'
    || process.env.REACT_APP_LIBRARY_METRICS === '1';
  if (!enabled) return null;
  return (tag, payload) => {
    console.info(tag, payload);
  };
}

/**
 * Create the process-wide Firestore instance with persistent cache when the
 * browser allows it. Safe to call more than once: the first result is reused.
 */
export function initializeFirestoreWithPersistence(app, deps = {}) {
  if (initialized && deps.forceNew !== true) {
    return initialized;
  }

  const initializeFirestore = deps.initializeFirestore;
  const getFirestore = deps.getFirestore;
  const persistentLocalCache = deps.persistentLocalCache;
  const persistentMultipleTabManager = deps.persistentMultipleTabManager;
  const memoryLocalCache = deps.memoryLocalCache;
  const logger = deps.logger === undefined ? defaultLogger() : deps.logger;

  let db = null;
  let state = defaultState();

  try {
    const localCache = persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    });
    db = initializeFirestore(app, { localCache });
    state = {
      mode: FIRESTORE_PERSISTENCE_MODES.PERSISTENT,
      enabled: true,
      multiTab: true,
      reason: null,
      initialized: true,
    };
  } catch (error) {
    const reason = sanitizeReason(error);
    try {
      if (typeof getFirestore === 'function') {
        db = getFirestore(app);
      } else if (isAlreadyInitialized(error)) {
        throw error;
      } else {
        db = initializeFirestore(app, {
          localCache: memoryLocalCache(),
        });
      }
      state = {
        mode: isAlreadyInitialized(error)
          ? FIRESTORE_PERSISTENCE_MODES.FALLBACK
          : FIRESTORE_PERSISTENCE_MODES.MEMORY,
        enabled: false,
        multiTab: false,
        reason,
        initialized: true,
      };
    } catch (fallbackError) {
      try {
        db = initializeFirestore(app, {
          localCache: memoryLocalCache(),
        });
        state = {
          mode: FIRESTORE_PERSISTENCE_MODES.MEMORY,
          enabled: false,
          multiTab: false,
          reason: sanitizeReason(fallbackError) || reason,
          initialized: true,
        };
      } catch (memoryError) {
        db = typeof getFirestore === 'function' ? getFirestore(app) : db;
        state = {
          mode: FIRESTORE_PERSISTENCE_MODES.FALLBACK,
          enabled: false,
          multiTab: false,
          reason: sanitizeReason(memoryError) || reason,
          initialized: true,
        };
      }
    }
  }

  logPersistenceState(state, logger);
  initialized = { db, state };
  return initialized;
}

export function getFirestorePersistenceState() {
  return initialized?.state || defaultState();
}

export function resetFirestorePersistenceForTests() {
  initialized = null;
}
